import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Spinner } from '@crm/ui';
import { Download, Plus, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ExportPdfButton, PageHeader, PrintButton, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { usePrintMode } from '@/lib/print-mode';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { type Breakdown, type ReportFilters, reportParams, useRevenueReport } from '../api';
import { defaultFilters, ReportFiltersBar } from '../components/report-filters';

interface Flags {
  includeSales: boolean;
  byProfessional: boolean;
  byService: boolean;
  byUnit: boolean;
}

const FLAG_LABELS: Record<keyof Flags, string> = {
  includeSales: 'Incluir lista de vendas',
  byProfessional: 'Total por profissional',
  byService: 'Total por servico',
  byUnit: 'Total por unidade',
};

function BreakdownCard({ title, rows }: { title: string; rows: Breakdown[] }) {
  return (
    <Card variant="flush">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Lista, nao tabela: sao tres campos e assim cabem em qualquer largura. */}
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">Sem dados.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {rows.map((row) => (
              <li key={row.id} className="flex items-baseline justify-between gap-3 px-4 py-2.5">
                <span className="min-w-0 truncate">{row.name}</span>
                <span className="flex shrink-0 items-baseline gap-3">
                  <span className="text-muted-foreground">{row.count}</span>
                  <span className="whitespace-nowrap font-medium">{formatCurrency(row.total)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Quantas vendas entram na tela por vez. */
const PAGINA = 50;

/** Faturamento por periodo, com os recortes ligados por bandeira. */
export function RevenueReportPage() {
  useDocumentTitle('Faturamento por periodo');

  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [flags, setFlags] = useState<Flags>({
    includeSales: true,
    byProfessional: true,
    byService: true,
    byUnit: false,
  });

  const report = useRevenueReport(filters, flags);
  const csvUrl = `/api/v1/reports/revenue.csv?${reportParams(filters)}`;

  /**
   * A lista de vendas de um periodo largo tem milhares de linhas: entra por
   * lotes. Os totais acima vem do servidor, sobre o periodo inteiro, e o CSV
   * sai completo.
   */
  const [visiveis, setVisiveis] = useState(PAGINA);
  const imprimindo = usePrintMode();
  const vendas = report.data?.sales ?? [];
  // No papel a lista sai inteira: paginacao e coisa de tela.
  const vendasVisiveis = imprimindo ? vendas : vendas.slice(0, visiveis);

  // biome-ignore lint/correctness/useExhaustiveDependencies: recomeca a cada consulta nova
  useEffect(() => setVisiveis(PAGINA), [report.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Wallet />}
        title="Faturamento por periodo"
        description="Somente atendimentos concluidos entram no faturamento."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Faturamento e vendas" />
            <Button asChild variant="outline">
              <a href={csvUrl} download>
                <Download className="h-4 w-4" />
                <span className="sm:hidden">CSV</span>
                <span className="hidden sm:inline">Exportar CSV</span>
              </a>
            </Button>
          </div>
        }
      />

      {report.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(report.error)}</p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard title="Faturamento" value={formatCurrency(report.data?.total ?? 0)} />
        <StatCard title="Atendimentos" value={report.data?.count ?? 0} />
        <StatCard title="Ticket medio" value={formatCurrency(report.data?.averageTicket ?? 0)} />
      </div>

      {/* O caminho do dinheiro no periodo: o que foi abatido e o que sobra.
          O filtro mora no mesmo quadro do resultado que ele muda. */}
      <Card variant="flush">
        <CardContent className="space-y-4 pb-6">
          <ReportFiltersBar value={filters} onChange={setFilters} />

          <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:flex sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
            {(Object.keys(FLAG_LABELS) as (keyof Flags)[]).map((flag) => (
              <Checkbox
                key={flag}
                label={FLAG_LABELS[flag]}
                checked={flags[flag]}
                onChange={(event) => setFlags({ ...flags, [flag]: event.target.checked })}
              />
            ))}
          </div>
        </CardContent>
        <CardContent className="pt-0">
          <ul className="divide-y divide-border text-sm">
            {[
              { label: 'Valor bruto dos servicos', value: report.data?.grossTotal ?? 0 },
              { label: 'Descontos', value: report.data?.discountTotal ?? 0, negative: true },
              { label: 'Cupons', value: report.data?.couponTotal ?? 0, negative: true },
              { label: 'Cashback usado', value: report.data?.cashbackTotal ?? 0, negative: true },
              { label: 'Recebido do cliente', value: report.data?.netTotal ?? 0, strong: true },
              {
                label: 'Comissoes a pagar',
                value: report.data?.commissionTotal ?? 0,
                negative: true,
              },
            ].map((line) => (
              <li
                key={line.label}
                className={`flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0 ${
                  line.strong ? 'font-semibold' : ''
                }`}
              >
                <span className="min-w-0 truncate">{line.label}</span>
                <span
                  className={`shrink-0 whitespace-nowrap ${
                    line.negative ? 'text-red-600 dark:text-red-400' : ''
                  }`}
                >
                  {line.negative ? '-' : ''}
                  {formatCurrency(line.value)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {report.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {report.data?.byProfessional ? (
              <BreakdownCard title="Total por profissional" rows={report.data.byProfessional} />
            ) : null}
            {report.data?.byService ? (
              <BreakdownCard title="Total por servico" rows={report.data.byService} />
            ) : null}
            {report.data?.byUnit ? (
              <BreakdownCard title="Total por unidade" rows={report.data.byUnit} />
            ) : null}
          </div>

          {report.data?.sales ? (
            <Card variant="flush">
              <CardHeader>
                <CardTitle className="text-base">Vendas do periodo</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Cada venda vira um cartao no celular; a tabela fica no desktop. */}
                <ul className="divide-y divide-border md:hidden">
                  {report.data.sales.length === 0 ? (
                    <li className="px-4 py-10 text-center text-muted-foreground">
                      Nenhuma venda no periodo.
                    </li>
                  ) : (
                    vendasVisiveis.map((row) => (
                      <li key={row.id} className="space-y-1 px-4 py-4">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="min-w-0 truncate font-medium">{row.customerName}</p>
                          <span className="whitespace-nowrap font-semibold">
                            {formatCurrency(row.total)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDateTime(row.startAt)}
                        </p>
                        <p className="text-sm">{row.services}</p>
                        {row.discountTotal > 0 ? (
                          <p className="text-sm text-muted-foreground">
                            Descontos {formatCurrency(row.discountTotal)}
                          </p>
                        ) : null}
                      </li>
                    ))
                  )}
                </ul>

                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">Quando</th>
                        <th className="px-4 py-3 font-medium">Cliente</th>
                        <th className="px-4 py-3 font-medium">Servicos</th>
                        <th className="px-4 py-3 text-right font-medium">Descontos</th>
                        <th className="px-4 py-3 text-right font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.data.sales.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                            Nenhuma venda no periodo.
                          </td>
                        </tr>
                      ) : (
                        vendasVisiveis.map((row) => (
                          <tr key={row.id} className="border-t border-border">
                            <td className="whitespace-nowrap px-4 py-3">
                              {formatDateTime(row.startAt)}
                            </td>
                            <td className="px-4 py-3">{row.customerName}</td>
                            <td className="px-4 py-3">{row.services}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right text-muted-foreground">
                              {formatCurrency(row.discountTotal)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-medium">
                              {formatCurrency(row.total)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {vendas.length > vendasVisiveis.length ? (
                  <div className="flex flex-col items-center gap-1 border-t border-border px-4 py-4">
                    <Button
                      variant="ghost"
                      className="w-full text-primary print:hidden sm:w-auto"
                      onClick={() => setVisiveis((atual) => atual + PAGINA)}
                    >
                      <Plus className="h-4 w-4" />
                      Mostrar mais
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
